/**
 * Format durations in milliseconds into YouTube-like timestamps
 * @param {Number} ms - duration in milliseconds
 * @returns {String} duration - display duration in hh:mm:ss format
 */
export function msToDisplayDuration(ms) {
  let seconds = (ms / 1000).toFixed(0);
  let minutes = Math.floor(seconds / 60);
  let hours = 0;

  if (minutes > 59) {
    hours = Math.floor(minutes / 60);
    minutes = minutes - hours * 60;
  }

  seconds = Math.floor(seconds % 60);

  if (hours) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(
      seconds
    ).padStart(2, '0')}`;
  } else {
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
}

/**
 * Format date time and relative time using unix timestamp
 * @param {Number} timestampUnix - unix timestamp
 * @param {String} countryCode - country code used for formatting and i18n
 * @param {Object} dtFormatOptions - optional, defaults to empty. other date time formatting options not included in this function.
 * @returns {Object}
 * @property {String} formattedTimestamp - timestamp after it's been formatted and translated
 * @property {Boolean} hasTimestampString - for bpages, indicates whether or not it needs a "posted" or "updated" string
 */
export function formatTimestamp(
  timestampUnix,
  countryCode,
  dtFormatOptions = {}
) {
  const nowUnix = Date.now() / 1000;
  const timeDiffUnix = nowUnix - timestampUnix;
  let formattedTimestamp;

  // display relative date for timestamps that are less than 24rs
  if (Intl.RelativeTimeFormat && timeDiffUnix < 86400) {
    const rtf = new Intl.RelativeTimeFormat(countryCode, {
      localeMatcher: 'best fit',
      numeric: 'always',
      style: 'long',
    });

    // special case for timestamps that are less than 1 hour
    if (timeDiffUnix < 3600) {
      const timeDiffMin = Math.round(timeDiffUnix / 60);
      formattedTimestamp = rtf.format(-timeDiffMin, 'minute');
    } else {
      const timeDiffHour = Math.round(timeDiffUnix / 3600);
      formattedTimestamp = rtf.format(-timeDiffHour, 'hour');
    }

    return { formattedTimestamp, hasTimestampString: true };
  }

  // the timestamp is more than 24 hours
  // or the relative formatting is not supported
  const timestampDate = new Date(timestampUnix * 1000);
  formattedTimestamp = new Intl.DateTimeFormat(countryCode, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...dtFormatOptions,
  }).format(timestampDate);

  return { formattedTimestamp, hasTimestampString: false };
}

/**
 * Check the validity of the date string
 * @param {String} date - {String} Date strings of the Date constructor
 * @return {Boolean} true on success, false if unable to convert with Date constructor
 */
export function validateDate(dateString) {
  return (new Date(dateString) !== 'Invalid Date') && !isNaN(new Date(dateString));
}

/**
 * Returns time since text
 * @param {String/Number} date - {String} Date strings of the Date constructor / {Number} unix timestamp
 * @return {Object} time since object {'type': '', 'value': '', 'text': ''}
 */
export function timeSince(date) {
  let interval;
  let seconds;
  const baseFormat = {
    'year': ['year', 'years', ],
    'month': ['month', 'months', ],
    'day': ['day', 'days', ],
    'hour': ['hour', 'hours', ],
    'minute': ['minute', 'minutes', ],
    'second': ['second', 'seconds', ],
  };

  if (typeof date === 'string' && validateDate(date)) {
    seconds = Math.floor(((new Date()).getTime() - (new Date(date)).getTime()) / 1000);
  } else if (typeof date === 'number' && (new Date(date)).getTime() > 0) {
    seconds = Math.floor(Date.now() / 1000 - date);
  }

  interval = Math.floor(seconds / 31536000);

  if (interval >= 1) {
    return {
      'type': 'year',
      'value': interval,
      'text': interval + ' ' + (interval === 1 ? baseFormat.year[0] : baseFormat.year[1]) + ' ago'
    };
  }

  interval = Math.floor(seconds / 2592000);

  if (interval >= 1) {
    return {
      'type': 'month',
      'value': interval,
      'text': interval + ' ' + (interval === 1 ? baseFormat.month[0] : baseFormat.month[1]) + ' ago'
    };
  }

  interval = Math.floor(seconds / 86400);

  if (interval >= 1) {
    return {
      'type': 'day',
      'value': interval,
      'text': interval + ' ' + (interval === 1 ? baseFormat.day[0] : baseFormat.day[1]) + ' ago'
    };
  }

  interval = Math.floor(seconds / 3600);

  if (interval >= 1) {
    return {
      'type': 'hour',
      'value': interval,
      'text': interval + ' ' + (interval === 1 ? baseFormat.hour[0] : baseFormat.hour[1]) + ' ago'
    };
  }

  interval = Math.floor(seconds / 60);

  if (interval > 1) {
    return {
      'type': 'minute',
      'value': interval,
      'text': interval + ' ' + (interval === 1 ? baseFormat.minute[0] : baseFormat.minute[1]) + ' ago'
    };
  }

  return {
    'type': 'second',
    'value': seconds,
    'text': seconds + ' ' + (seconds === 1 ? baseFormat.minute[0] : baseFormat.minute[1]) + ' ago'
  };
}

/**
 * Returns time since text according to country code
 * @param {String/Number} date - {String} date in UTC format `yy:mm:ddThh:mm:ssZ / {Number} unix timestamp`
 * @param {String} countryCode - country code used for formatting and i18n
 * @return {string}
 */
export function relativeTimeSince(date, countryCode = 'en') {
  const {type, value, text} = timeSince(date);
  if (!Intl.RelativeTimeFormat) {
    return text;
  }
  const rtf = new Intl.RelativeTimeFormat(countryCode, {
    localeMatcher: 'best fit',
    numeric: 'always',
    style: 'long',
  });
  return rtf.format(-value, type);
}
